/**
 * Local Auth API Service
 *
 * Authentication-related API calls for local authentication.
 * Aligned with backend OpenAPI specification.
 */

import api from './api'

// --- Types ---

export interface LoginDto {
    email: string
    password: string
}

/** Organization info returned from login */
export interface OrganizationInfo {
    id: string
    name: string
    /** RBAC role UUIDs assigned to this membership */
    roleIds: string[]
    isDefault: boolean
}

/** Login response with multi-organization support */
export interface LoginResponseDto {
    access_token: string
    refresh_token: string
    expires_in: number
    organizations: OrganizationInfo[]
}

export interface SendMagicLinkDto {
    email: string
    organizationId?: string
}

export interface SetPasswordDto {
    token: string
    password: string
}

export interface RequestPasswordResetDto {
    email: string
}

export interface ChangePasswordDto {
    currentPassword: string
    newPassword: string
}

export interface InviteUserDto {
    userId: string
}

export interface InviteUserResponseDto {
    success: boolean
    message: string
    userId?: string
}

export interface SuccessResponseDto {
    success: boolean
    message: string
}

export interface SelectOrganizationDto {
    organizationId: string
}

export interface SelectOrganizationResponseDto {
    access_token: string
    expires_in: number
    organization: OrganizationInfo
}

export interface RefreshTokenDto {
    refresh_token: string
}

export interface RefreshResponseDto {
    access_token: string
    expires_in: number
}

/** Membership info from user profile */
export interface MembershipDto {
    id: string
    organizationId: string
    organizationName: string
    /** RBAC role UUIDs assigned to this membership */
    assignedRoleIds: string[]
    /** Human-readable RBAC role names (for display) */
    assignedRoleNames: string[]
    status: 'provisioned' | 'invited' | 'active' | 'disabled'
    isDefault: boolean
}

export interface UserProfileDto {
    id: string
    email: string
    firstName?: string
    lastName?: string
    /** Whether user is a Super Admin with global platform access */
    isSuperAdmin: boolean
    organizationId: string
    status?: 'provisioned' | 'invited' | 'active' | 'disabled'
    isMachine: boolean
    /** IANA timezone string, e.g. "America/Chicago" */
    timezone?: string
    memberships?: MembershipDto[]
}

export interface UserProfileResponseDto {
    ok: boolean
    data: UserProfileDto
}

// --- API Functions ---

/**
 * Login with email and password
 */
export async function login(credentials: LoginDto): Promise<LoginResponseDto> {
    const response = await api.post<LoginResponseDto>('/auth/login', credentials)
    return response.data
}

/**
 * Send magic link email for passwordless login
 */
export async function sendMagicLink(data: SendMagicLinkDto): Promise<SuccessResponseDto> {
    const response = await api.post<SuccessResponseDto>('/auth/magic-link', data)
    return response.data
}

/**
 * Verify magic link token and return JWT
 */
export async function verifyMagicLink(token: string): Promise<LoginResponseDto> {
    const response = await api.get<LoginResponseDto>('/auth/magic-link/callback', {
        params: { token },
    })
    return response.data
}

/**
 * Request password reset email
 */
export async function requestPasswordReset(data: RequestPasswordResetDto): Promise<SuccessResponseDto> {
    const response = await api.post<SuccessResponseDto>('/auth/reset-password', data)
    return response.data
}

/**
 * Set password using invitation or reset token
 */
export async function setPassword(data: SetPasswordDto): Promise<SuccessResponseDto> {
    const response = await api.post<SuccessResponseDto>('/auth/set-password', data)
    return response.data
}

/**
 * Change password (requires current password)
 */
export async function changePassword(data: ChangePasswordDto): Promise<SuccessResponseDto> {
    const response = await api.post<SuccessResponseDto>('/auth/change-password', data)
    return response.data
}

/**
 * Invite a new user to an organization (Admin only)
 */
export async function inviteUser(data: InviteUserDto): Promise<InviteUserResponseDto> {
    const response = await api.post<InviteUserResponseDto>('/auth/invite', data)
    return response.data
}

/**
 * Get current authenticated user profile
 */
export async function getCurrentUserProfile(): Promise<UserProfileDto | null> {
    try {
        const response = await api.get<UserProfileResponseDto>('/auth/me')
        return response.data.data || null
    } catch (error) {
        console.error('Failed to fetch current user profile:', error)
        return null
    }
}

/**
 * Switch to a different organization
 */
export async function selectOrganization(organizationId: string): Promise<SelectOrganizationResponseDto> {
    const response = await api.post<SelectOrganizationResponseDto>('/auth/select-organization', { organizationId })
    return response.data
}

/**
 * Refresh access token
 */
export async function refreshAccessToken(refreshToken: string): Promise<RefreshResponseDto> {
    const response = await api.post<RefreshResponseDto>('/auth/refresh', { refresh_token: refreshToken })
    return response.data
}

/** Resolved permission for a single module */
export interface ModulePermissionDto {
    accessLevel: 'none' | 'view_only' | 'standard' | 'full'
    rowScope: 'explicit' | 'own' | 'team' | 'all'
}

/** Role info returned with permissions */
export interface RoleInfoDto {
    id: string
    name: string
}

/** Response from GET /auth/me/permissions */
export interface PermissionsResponseDto {
    modules: Record<string, ModulePermissionDto>
    roles?: RoleInfoDto[]
}

/**
 * Fetch resolved permissions for the current user
 */
export async function fetchPermissions(): Promise<PermissionsResponseDto | null> {
    try {
        const response = await api.get<{ ok: boolean; data: PermissionsResponseDto }>('/auth/me/permissions')
        return response.data.data || null
    } catch (error) {
        console.error('Failed to fetch permissions:', error)
        return null
    }
}

/**
 * Logout and revoke refresh token
 */
export async function logoutApi(refreshToken: string): Promise<void> {
    await api.post('/auth/logout', { refresh_token: refreshToken })
}

export default {
    login,
    sendMagicLink,
    verifyMagicLink,
    requestPasswordReset,
    setPassword,
    changePassword,
    inviteUser,
    getCurrentUserProfile,
    selectOrganization,
    refreshAccessToken,
    fetchPermissions,
    logoutApi,
}

